import { LoanTypeEnum } from '../../../models/enum/loan-type.enum';
import { P2Alert, P2TextDescription, P2TextInput } from 'react-bootstrap-common-script';
import { Radio_Loan_Types } from '../../../utils/shared-radio';
import RadioGroup from '../../form/grouped/radio/radio-group';
import { InfoCard } from '../../info';
import { InfoCircle } from '../../../assets/svg';
import { TextInfo } from '../../text';
import { useCallback, useMemo, useState } from 'react';
import { OwnershipReasonEnum } from '../../../models/enum/ownership-reason.enum';
import { FlatRoomTypeEnum } from '../../../models/enum/flat-room-type.enum';
import { HDBLinks } from '../../../utils/shared/links';
import { Helpers } from '../../../utils/helper';
import { Dropdown } from '../../dropdown';
import { ApplicationStep2 } from '../../../models/classes/ApplicationStep2.class';
import { useGetBankCodesQuery } from '../../../redux/services/getBankCodes';
import { useAppSelector } from '../../../redux/hook';
import { DropdownItemInterface } from '../../../models/interfaces/dropdown-item.interface';

interface FinancingFormProps {  
  variant?:"default" | "review";
  alertNumber?: number;
  forceError?: boolean;
  isFlatResale?: boolean;
  flat_type?: FlatRoomTypeEnum;
  change_ownership_reason?: OwnershipReasonEnum;
  has_outgoing_owner?:boolean;
  current_loan_type?: LoanTypeEnum;
  new_loan_type?: LoanTypeEnum;
  applicationStep2FormData: ApplicationStep2;
  show_new_loan_info?:boolean; //This will be used for STAGE B Reviewing page.
  onNewLoanTypeChange: (value: string) => void;
  onNewLoanAmountChange: (value: string) => void;
  handleMultipleFinancialValueChange: (newValues: Partial<ApplicationStep2>) => void
}

function FinancingForm({isFlatResale=false, variant="default", has_outgoing_owner=false, change_ownership_reason=undefined, flat_type=undefined,alertNumber=0, ...props }: FinancingFormProps) {
  // States
  const singpassToken = useAppSelector(state => state.auth.AuthenticationSlice.singpassToken);
  const { data: bankCodesData } = useGetBankCodesQuery(
    { singpass_token: singpassToken ?? ""},
  );
  const [isDisplayingDefault, setIsDisplayingDefault] = useState(true);

  // Derived values
  const { new_loan_type, new_loan_amount, bank_code } = props.applicationStep2FormData;

  const dropdownItems: DropdownItemInterface[] = useMemo(() => {
    if (!bankCodesData) return [];

    return bankCodesData.bank_details.map((bank_detail, index) => ({
      id: index,
      title: bank_detail.bank_name,
      value: bank_detail.bank_code,
    }));
  }, [bankCodesData]);

  const formattedNewLoanAmount = useMemo(() => {
    try{
      if (new_loan_amount?.toString() === "0") {
        return "$ 0.00";
      } else {
        if (isDisplayingDefault) {
          return Helpers.formatThousandSeparator(Helpers.formatCurrencyInput(new_loan_amount?.toString() ?? "0", true), "$ ");
        } else {
          return Helpers.formatThousandSeparator(new_loan_amount?.toString(), "$ ");
        }
      }
    } catch(e){
      return "$ 0.00";
    }
  }, [new_loan_amount, isDisplayingDefault])

  let loan_type_title: string = "";
  switch(new_loan_type){
      case LoanTypeEnum.hdb_loan:
          loan_type_title = "HDB Loan";
          break;
      case LoanTypeEnum.bank_loan:
          loan_type_title = "Bank Loan";
          break;
      default: 
          loan_type_title = "No Loan";
          break;
  }
  const isFreshLoan = useMemo(()=>{
    return props.current_loan_type === LoanTypeEnum.no_loan;
  },[ props.current_loan_type ])

  const newLoanDescription = useMemo(()=>{
    return `To effect a change in flat ownership, the current owner(s) must discharge any existing mortgage loan. The proposed owner(s) may need to take a new loan to finance any outstanding mortgage and refund any CPF monies used by the current owner(s) for the mortgage.`
    // return `As part of the process, the current outstanding loan under the current owners' account will have to be closed. Proposed owners may obtain a new loan to finance the outstanding mortgage.${has_outgoing_owner ? ' Proposed owner(s) would need to refund the amount of CPF savings used by the outgoing owner(s).' : ''}` // Commented out to be deleted 1 aug 2025
  },[has_outgoing_owner])

  const handleNewLoanTypeChange = useCallback((value: LoanTypeEnum) => {
    if (value !== LoanTypeEnum.bank_loan) {
      props.handleMultipleFinancialValueChange({bank_code: "", bank_name: "", new_loan_amount: "0", new_loan_type: value})
    } else {
      props.onNewLoanTypeChange(value)
    }
  }, [props.onNewLoanTypeChange, props.handleMultipleFinancialValueChange])
  
  const handleOnBankSelect = useCallback((bankCode: string) => {    
    const bankName = bankCodesData?.bank_details?.find(bankDetail => bankDetail.bank_code === bankCode)?.bank_name ?? ""

    props.handleMultipleFinancialValueChange({bank_code: bankCode, bank_name: bankName})
  }, [bankCodesData, props.handleMultipleFinancialValueChange])
  
  const handleOnNewLoanAmountBlur = useCallback((e:any)=> {
    if (e) {
      let value = Helpers.formatCurrencyInput(e, true);
      if (props.onNewLoanAmountChange) {
        props.onNewLoanAmountChange(value);
      }
    }
  },[props])

  const handleOnNewLoanAmountChange = useCallback((e: string) => {
    setIsDisplayingDefault(false);
    if (props.onNewLoanAmountChange) {
      props.onNewLoanAmountChange(e);
    }
    let value = Helpers.formatCurrencyInput(e, false);
    if (props.onNewLoanAmountChange) {
      props.onNewLoanAmountChange(value);
    }
  },[props])

  // NOTE : if show_new_loan_info is set to true, mean it will only show the info title for the loan type 
  if( props.show_new_loan_info ){
    return (
      <TextInfo title='New Loan Type:' description={<div><p className='m-0'>{loan_type_title}</p></div>} />
    )
  }

  return (
    <div>
      <P2TextDescription color='#000' title='New Loan Information' />
      <div style={{ marginTop: 24 }}>
        <P2TextDescription color='#000' description={newLoanDescription} />
      </div>

      <RadioGroup style={{ marginTop: 24 }} layout='inline' items={Radio_Loan_Types} onItemSelect={handleNewLoanTypeChange} value={new_loan_type} label={'Type of Loan Required'} required />

      {isFreshLoan && new_loan_type === LoanTypeEnum.bank_loan && <div style={{ marginTop: 32 }}>
        <InfoCard style={{ backgroundColor: "#FFF" }} icon={<InfoCircle />} description={"Proposed owners would need to obtain the bank's letter of offer to complete the application."} />
      </div>}

      {new_loan_type === LoanTypeEnum.no_loan && <div style={{ marginTop: 32 }}>
        <InfoCard style={{ backgroundColor: "#FFF" }} icon={<InfoCircle />} description={<p>If you require a loan at the later stage of the application process, you will be required to cancel the application and re-submit a new application.</p>} />
      </div>}
      {props.forceError && !props.new_loan_type &&      
        <div style={{ marginTop: 24 }}>
          <P2Alert variant={"danger"} text={"This is a required field."}></P2Alert>
        </div>
      }

      {isFreshLoan && change_ownership_reason === OwnershipReasonEnum.divorce && new_loan_type === LoanTypeEnum.hdb_loan && <div style={{ marginTop: 32 }}>
        <InfoCard style={{ backgroundColor: "#FFF" }} icon={<InfoCircle />} description={"HDB does not grant a loan for any cash consideration stated in the divorce documents."} />
      </div>}
      {/* isFreshLoan removed, new logic change to isFlatResale && new_loan = hdb */}
      {isFlatResale && new_loan_type === LoanTypeEnum.hdb_loan && <div style={{ marginTop: 32 }}>
        <InfoCard style={{ backgroundColor: "#FFF" }} icon={<InfoCircle />} description={`As the proposed owners require a new HDB loan, they would be required to obtain a current market valuation report to determine the mortgage loan ceiling as the flat was bought from the resale market. Valuation fee applies.`} />
      </div>}

      {new_loan_type === LoanTypeEnum.bank_loan ? (
        <>
          <div style={{ marginTop: 24, marginBottom: 24 }}>
            <Dropdown
              variant="small"
              selectedDropdown={bank_code}
              setSelectedDropdown={handleOnBankSelect}
              items={dropdownItems}
              placeholder="Please select bank name"
              label="Bank name"
              required={true}
            />
          </div>

          <div style={{ marginBottom: 24 }}>
            <P2TextInput
              isError={false}
              errorMsg={""}
              maxLength={30}
              required
              value={formattedNewLoanAmount}
              name="new_loan_amount"
              label={"New bank loan amount"}
              type="text"
              onBlur={handleOnNewLoanAmountBlur}
              onChange={handleOnNewLoanAmountChange}
            />
          </div>
        </>
      ) : null}

      {alertNumber !== 0 &&      
      <div className='polymer-shared-alert-container'>
          <div style={{ marginTop: 24 }}>
            {alertNumber === 1  && <P2Alert variant={"success"} text={"Based on the information you have provided above, you are eligible to proceed with the application for Change in Flat Ownership, subject to document verification by HDB."}></P2Alert>}
            {(alertNumber === 2 || alertNumber === 3)  && <P2Alert variant={"danger"} text={<div>It appears that you do not have an approved HDB Loan Eligibility (HLE) letter. Please <a href={HDBLinks.apply_hle_url} className="polymer-text-links">apply for an HLE letter</a> first as HLE applications will take 21 working days for processing. You may continue with the application for change in flat ownership and proceed to submit once HLE has been obtained.</div>}></P2Alert>}
            {alertNumber === 4 && <P2Alert variant={"danger"} text={<div>We are temporarily unable to verify your HDB Loan Eligibility (HLE) letter. Please try again later. If the problem continues, contact HDB for assistance.</div>}></P2Alert>}
            {alertNumber === 5 && <P2Alert variant={"danger"} text={<div>There are multiple loan eligibility applications and unable to proceed with the ownership change application. Please approach HDB for assistance.</div>}></P2Alert>}
          </div>
       </div>}
 

      {/* <div style={{ marginTop: 24 }}>
        <P2TextDescription color='#000' description={<p className='m-0'>If you want to estimate the loan amount needed for changing flat ownership, check out our <a href='#'>Financial Planning Calculator</a>. It's quick, easy, and we're here to help if you need it!</p>} />
      </div> */}


    </div>
  )
}

export default FinancingForm